import { IdToken, useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useState } from 'react';
import { demoSvcAudience, demoSvcScopes, redirectUri } from '../../config';
import { MainLogo } from '../components/MainLogo';
import { JSONTree } from 'react-json-tree';
import './Home.scss';

export function Home() {
	const {
		isLoading,
		isAuthenticated,
		error
	} = useAuth0();

	if (isLoading) return <div>Loading...</div>;

	return <>
		<VerifyEmailModal error={error || null} />

		<nav className="header">
			<div className="header-left">
				<MainLogo className="header-logo" />
				<span className="header-title">Demo App with RitchieID/Auth0 Integration</span>
			</div>

			<div className="header-right">
				<LoginLogoutButton isAuthenticated={isAuthenticated} />
				<SignUpButton isAuthenticated={isAuthenticated}/>
			</div>
		</nav>

		<div className="body">
			<DecodedTokens isAuthenticated={isAuthenticated} />
		</div>
	</>;
}

function VerifyEmailModal(props: { error: Error | null }) {
	const [error, setError] = useState<Error | null>(props.error)
	const { loginWithRedirect } = useAuth0();
	const onClick = useCallback(() => {
		loginWithRedirect({
			redirectUri,
			max_age: 0
		});
	}, [loginWithRedirect]);

	useEffect(() => {
		if (!error) return;
		window.history.pushState({}, "URL Rewrite Example", window.location.origin);
	}, [error]);

	const exit = useCallback(() => {
		setError(null);
	}, [setError]);

	if (!error) return null;

	return (
		<div className="modal">
			<div className="modal-content">
				<button
					className="modal-exit"
					onClick={exit}
				>X</button>
				<span className="modal-text">{error.message}</span>
				<span className="modal-secondary-text">or</span>
				<button
					className="modal-login-with-other-account-btn"
					onClick={onClick}
				>Login with a Different Account</button>
			</div>
		</div>
	);
}

function SignUpButton({ isAuthenticated }: { isAuthenticated: boolean }) {
	const { loginWithRedirect } = useAuth0();
	const onClick = useCallback(() => {
		loginWithRedirect({
			redirectUri,
			screen_hint: 'signup',
			max_age: 0
		});
	}, [loginWithRedirect]);

	if (isAuthenticated) return null;

	return (
		<button
			onClick={onClick}
			className="signup-btn"
		>Sign Up</button>
	);
}

function LoginLogoutButton({ isAuthenticated }: { isAuthenticated: boolean }) {
	const { loginWithRedirect, logout } = useAuth0();
	const loginLogoutText = (isAuthenticated) ? 'Logout' : 'Login';

	const onClick = useCallback(() => {
		if(isAuthenticated) return logout({ returnTo: redirectUri });

		loginWithRedirect({ redirectUri });
	}, [loginWithRedirect, isAuthenticated, logout]);

	return (
		<button
			onClick={onClick}
			className="login-btn logout-btn"
		>{loginLogoutText}</button>
	);
}

function DecodedTokens({ isAuthenticated }: { isAuthenticated: boolean }) {
	const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
	const [opaqueAccessToken, setOpaqueAccessToken] = useState<{ token: string } | null>(null);
	const [jwtAccessToken, setJwtAccessToken] = useState<{ raw: string, claims: Record<string, any> } | null>(null);
	const [idTokenClaims, setIdTokenClaims] = useState<{ claims: IdToken } | null>(null);
	const shouldExpandNode = useCallback(() => true, []);
	const valueRenderer = useCallback((valueAsString: string, value: any, ...key: Array<string | number>) => {
		return <span className={key.reverse().join('-')}>{valueAsString}</span>
	}, []);

	useEffect(() => void async function(){
		if (!isAuthenticated) return;

		try {
			const opaqueToken = await getAccessTokenSilently({ ignoreCache: true });
			const jwtToken = await getAccessTokenSilently({
				audience: demoSvcAudience,
				scope: demoSvcScopes,
				ignoreCache: true
			});
			const idTokenClaims = await getIdTokenClaims();

			const accessTokenClaims = JSON.parse(window.atob(jwtToken.split('.')[1]));

			setOpaqueAccessToken({ token: opaqueToken });
			setJwtAccessToken({ raw: jwtToken, claims: accessTokenClaims });
			setIdTokenClaims({ claims: idTokenClaims });
		} catch (err) {
			console.log(err);
		}
	}(), [getAccessTokenSilently, getIdTokenClaims, isAuthenticated]);

	if(!isAuthenticated) {
		return (
			<div className="decoded-tokens">You are not authenticated, please login or signup</div>
		);
	}

	return (
		<div className="decoded-tokens">
			<h2 className="decoded-tokens-title"><strong>Access Token: </strong></h2>
			<h4 className="decoded-tokens-title"><strong>Opaque: </strong></h4>
			<div>
				This access token is opaque, meaning that it does not contain any information encoded in it.
				It can be used as a Bearer token to obtain an ID token from the Auth0 <i>/userinfo</i> endpoint.

				<br/><br/>

				Auth0 issues opaque access tokens when the <i>openid</i> scope is specified without any audience in the request for an access token. When the <i>openid</i> scope is specified, Auth0 will issue an ID token.

				<br/><br/>

				Single Page Apps that don't access an API should request opaque access tokens.
			</div>
			<div className='opaque-access-token'>
				<JSONTree
					shouldExpandNode={shouldExpandNode}
					theme={'default'}
					data={opaqueAccessToken}
					hideRoot={true}
					valueRenderer={valueRenderer}
				/>
			</div>
			<br />

			<h4 className="decoded-tokens-title"><strong>JSON Web Token (JWT): </strong></h4>
			<div>
				This access token is a JSON Web Token (JWT), meaning that it contains information encoded in it.
				It can be used as a Bearer token to obtain an ID token from the Auth0 <i>/userinfo</i> endpoint and can also be used as a Bearer token to access the API (i.e. audience) for which it was requested.

				<br/><br/>

				Auth0 issues JWT access tokens when an audience is specified in the request for the token. Just like opaque access tokens, JWT access tokens can be used to fetch an ID Token from the <i>/userinfo</i> endpoint as long as the <i>openid</i> scope is specified.

				<br/><br/>

				The scopes (i.e. permissions) of which actions can be performed with this access token are encoded within the token itself.  In addition to the scopes, the access token contains other information such as which API the token can be used with (<i>aud</i>, abbreviation for "audience") and the ID of which user the access token belongs to (<i>sub</i>, abbreviation for "subject").

				<br/><br/>

				Single Page Apps that access an API need to specify the given API's audience when requesting the access token.  Each API has a unique audience and if a SPA needs to access multiple APIs, multiple requests are required to get an access token for each API.
			</div>
			<div className='jwt-access-token'>
				<JSONTree
					shouldExpandNode={shouldExpandNode}
					theme={'default'}
					data={jwtAccessToken}
					hideRoot={true}
					valueRenderer={valueRenderer}
				/>
			</div>
			<br />

			<h2 className='decoded-tokens-title'><strong>ID Token:</strong></h2>
			<div>
				The ID token is a JSON Web Token that contains encoded information about the user.
				The top-level fields (i.e. claims) from the token's decoded payload can be seen below
				(<strong>Note:</strong> The <i>__raw</i> field contains the original/undecoded ID token):
			</div>
			<div className='id-token'>
				<JSONTree
					shouldExpandNode={shouldExpandNode}
					theme={'default'}
					data={idTokenClaims}
					hideRoot={true}
					valueRenderer={valueRenderer}
				/>
			</div>
		</div>
	);
}